import { DotsHorizontal, MessageChatCircle } from '@untitledui/icons';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';
import { fetchWithDefaults } from '@/lib/http/client';
import { ConversationRunStateLabel } from '@/lib/reasoning/components/ConversationRunStateLabel';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';
import { deriveConversationRunState } from '@/lib/reasoning/conversationRunState';
import { useViewHealthMarker } from '@/lib/view-health';
import { useNewConversationComposer } from '@/pages/reasoning/useNewConversationComposer';

import { HeroChatInput } from '../../components/reasoning/HeroChatInput';
import { SuggestionBadges } from '../../components/reasoning/SuggestionBadges';
import { useOrg } from '../../contexts/OrgContext';
import { useTitle } from '../../hooks/useTitle';

interface ConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: ConversationLifecycleState;
  created_at: string;
  updated_at: string;
  total_chains: number;
  has_active_chain?: boolean;
  last_chain_failed?: boolean;
}

export default function ConversationsHome() {
  useTitle('Ahoy');

  const navigate = useNavigate();
  const { orgId } = useParams();
  const { currentOrg } = useOrg();
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Composer state + submit lifecycle, shared with the builder route.
  const {
    message,
    setMessage,
    uploadedFiles,
    setUploadedFiles,
    submitting,
    error,
    submitMessage,
    handleSubmit,
  } = useNewConversationComposer();

  useViewHealthMarker('chat-list-ready', {
    ready: !loading && !loadError && conversations.length > 0,
  });
  useViewHealthMarker('chat-list-empty-ready', {
    ready: !loading && !loadError && conversations.length === 0,
  });

  const loadConversations = async () => {
    if (!orgId) {
      return;
    }

    try {
      setLoading(true);
      setLoadError(false);
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/`
      );

      if (!response.ok) {
        throw new Error('Failed to load conversations');
      }

      const data = await response.json();
      // Show only the 3 most recent conversations
      setConversations((data.conversations || []).slice(0, 3));
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      loadConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadConversations is intentionally omitted; refetch should only run when orgId changes
  }, [orgId]);

  const handleSuggestionSelect = useCallback(
    (text: string) => {
      submitMessage(text);
    },
    [submitMessage]
  );

  const handleDeleteConversation = async (conversationId: string) => {
    if (!orgId) {
      return;
    }

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete conversation');
      }

      // Reload conversations
      loadConversations();
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error("We couldn't delete this conversation. Try again.");
    }
  };

  const orgIdValue = orgId || currentOrg?.id || '';
  let parsedOrgId: number | undefined;
  if (!orgIdValue) {
    parsedOrgId = undefined;
  } else if (typeof orgIdValue === 'number') {
    parsedOrgId = orgIdValue;
  } else {
    parsedOrgId = parseInt(orgIdValue, 10);
  }

  return (
    <div className='min-h-full gradient-bg'>
      <div className='max-w-3xl mx-auto px-4 py-8 sm:py-12 lg:py-16 space-y-6 sm:space-y-8'>
        {/* Hero Section */}
        <div className='space-y-6 sm:space-y-8 text-center'>
          {/* Ahoy Logo */}
          <div className='flex justify-center mb-8'>
            <AhoyLogoMinimal className='w-[60px] h-[60px]' />
          </div>

          <h1 className='text-[35px] font-normal leading-tight text-[var(--color-logo)] mb-6'>
            What's on deck today?
          </h1>

          {/* Hero Input — shared HeroChatInput (hero box + wave behind). */}
          <HeroChatInput
            value={message}
            onChange={setMessage}
            onSubmit={handleSubmit}
            placeholder='Ask a question, draft an email, build a report'
            loading={submitting}
            error={error}
            enableVoice={true}
            autoFocus
            orgId={parsedOrgId}
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
          />

          {/* Suggestion Badges */}
          <SuggestionBadges onSelect={handleSuggestionSelect} />
        </div>

        {/* Recent Chats — skeleton while loading, fade-in when loaded */}
        {orgId && loading && (
          <div className='space-y-4'>
            <div className='border-t border-border-primary pt-4' />
            <div className='flex items-center justify-between'>
              <div className='h-4 w-24 bg-base-300 rounded animate-pulse' />
              <div className='h-4 w-12 bg-base-300 rounded animate-pulse' />
            </div>
            <div className='space-y-0'>
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className='flex items-center gap-4 py-2 -mx-4 px-4'
                >
                  {/* MessageChatCircle icon - matches: size-6 stroke-fg-primary */}
                  <div className='size-6 bg-base-300 rounded animate-pulse flex-shrink-0' />
                  {/* Title - matches: text-sm font-medium flex-1 */}
                  <div className='flex-1 h-4 bg-base-300 rounded animate-pulse' />
                  {/* Timestamp - matches: text-sm font-medium text-secondary */}
                  <div className='w-16 h-4 bg-base-300 rounded animate-pulse' />
                  {/* DotsHorizontal menu button - matches: size-6 in p-1 button */}
                  <div className='size-6 bg-base-300 rounded animate-pulse' />
                </div>
              ))}
            </div>
          </div>
        )}
        {orgId && !loading && conversations.length > 0 && (
          <div className='space-y-4 animate-[fadeIn_300ms_ease-out]'>
            <div className='border-t border-border-primary pt-4' />
            <div className='flex items-center justify-between'>
              <h2 className='text-sm font-semibold text-black dark:text-white'>
                Recent chats
              </h2>
              <button
                onClick={() => {
                  const path = orgIdValue
                    ? `/${orgIdValue}/conversations/history`
                    : `/conversations/history`;
                  navigate(path);
                }}
                className='text-sm font-semibold text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors cursor-pointer'
              >
                See all
              </button>
            </div>
            <div className='space-y-0'>
              {conversations.map((conv, index) => (
                <div
                  key={conv.id}
                  className='flex items-center gap-4 py-2 cursor-pointer hover:bg-bg-secondary/50 transition-colors -mx-4 px-4 animate-[fadeIn_300ms_ease-out]'
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: 'both',
                  }}
                  onClick={() => {
                    const path = orgIdValue
                      ? `/${orgIdValue}/conversations/${conv.id}`
                      : `/conversations/${conv.id}`;
                    navigate(path);
                  }}
                >
                  <MessageChatCircle
                    className='size-6 stroke-fg-primary flex-shrink-0'
                    strokeWidth={2}
                  />
                  <p className='text-sm font-medium text-black dark:text-white flex-1'>
                    {conv.title || 'Untitled conversation'}
                  </p>
                  {(() => {
                    const runState = deriveConversationRunState({
                      hasActiveChain: conv.has_active_chain,
                      lastChainFailed: conv.last_chain_failed,
                    });
                    return runState === 'idle' ? (
                      <p className='text-xs font-medium text-neutral-500 dark:text-neutral-400'>
                        {formatDistanceToNow(new Date(conv.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    ) : (
                      <ConversationRunStateLabel state={runState} />
                    );
                  })()}
                  <div className='relative'>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === conv.id ? null : conv.id);
                      }}
                      className='p-1 hover:bg-bg-secondary rounded transition-colors cursor-pointer'
                      title='Conversation options'
                    >
                      <DotsHorizontal
                        className='size-6 stroke-fg-primary'
                        strokeWidth={2}
                      />
                    </button>
                    {openMenuId === conv.id && (
                      <>
                        {/* Backdrop to close menu */}
                        <div
                          className='fixed inset-0 z-10'
                          onClick={e => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                          }}
                        />
                        {/* Dropdown menu */}
                        <div className='absolute right-0 top-full mt-1 bg-bg-primary border border-border-secondary rounded-lg shadow-lg py-1 z-20 min-w-[160px]'>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteConversation(conv.id);
                            }}
                            className='w-full text-left px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors cursor-pointer'
                          >
                            Delete conversation
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
